import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleDollarSign, Download, ExternalLink, Inbox, Search, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty";
import { Pagination } from "@/components/pagination";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { bulkDeleteLeads, deleteLead, exportCsvUrl, fieldsUnion, getLead, searchLeads, visitTrailForLead, type LeadFilters } from "@/api/leads";
import { apiErrorMessage } from "@/lib/api";
import { SideDrawer, DrawerSection, DrawerRow } from "@/components/side-drawer";
import { RecordFtdDialog } from "@/components/record-ftd-dialog";
import { CapturedData } from "@/components/captured-data";
import { formatDate } from "@/lib/utils";
import { listDomains } from "@/api/domains";
import { listCampaignsPaged } from "@/api/campaigns";
import { formatRelativeTime } from "@/lib/utils";
import type { LeadDto } from "@/types/api";
import { AdvertiserSelect } from "@/components/advertiser-select";
import { AffiliateSelect } from "@/components/affiliate-select";
import {
    AdvertiserCell,
    AffiliateCell,
    useAdvertiserLookup,
    useAffiliateLookup,
} from "@/components/advertiser-cell";
import { ownerFilterFromUrl, ownerFilterParam, ownerFilterToUrl } from "@/api/commerce";

const PAGE_SIZE = 50;

export function LeadsPage(): React.ReactNode {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [page, setPage] = React.useState(0);
    // Bulk-select for test/spam cleanup. Ids are global, so selection survives
    // paging — the header checkbox only reflects the rows on THIS page.
    const [selected, setSelected] = React.useState<Set<number>>(new Set());
    const [domainId, setDomainId] = React.useState<number | "all">("all");
    const [campaignId, setCampaignId] = React.useState<number | "all">("all");
    const [emailContains, setEmailContains] = React.useState("");
    const [phoneContains, setPhoneContains] = React.useState("");
    // Debounced mirrors of the free-text filters — these (not the raw inputs)
    // feed the query key, so typing "gmail.com" fires one request, not nine.
    const [emailDebounced, setEmailDebounced] = React.useState("");
    const [phoneDebounced, setPhoneDebounced] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => setEmailDebounced(emailContains.trim()), 250);
        return () => clearTimeout(t);
    }, [emailContains]);
    React.useEffect(() => {
        const t = setTimeout(() => setPhoneDebounced(phoneContains.trim()), 250);
        return () => clearTimeout(t);
    }, [phoneContains]);
    const [primarySource, setPrimarySource] = React.useState<string | null>(null);
    const [selectedLead, setSelectedLead] = React.useState<LeadDto | null>(null);

    // Advertiser + Affiliate filters, URL-persisted alongside the existing
    // `?id=` deep-link param. undefined = "All", null = "Unassigned" (wire -1).
    const advertiserId = ownerFilterFromUrl(searchParams.get("advertiserId"));
    const affiliateId = ownerFilterFromUrl(searchParams.get("affiliateId"));
    const setAdvertiserId = React.useCallback(
        (next: number | null | undefined): void => {
            setSearchParams(
                (p) => {
                    const v = ownerFilterToUrl(next);
                    if (v == null) p.delete("advertiserId");
                    else p.set("advertiserId", v);
                    return p;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );
    const setAffiliateId = React.useCallback(
        (next: number | null | undefined): void => {
            setSearchParams(
                (p) => {
                    const v = ownerFilterToUrl(next);
                    if (v == null) p.delete("affiliateId");
                    else p.set("affiliateId", v);
                    return p;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );
    const advLookup = useAdvertiserLookup();
    const affLookup = useAffiliateLookup();
    const showAdvertiserColumn = advLookup.advertisers.length > 0;
    const showAffiliateColumn = affLookup.affiliates.length > 0;

    // Deep-link support: /leads?id=42 opens the lead drawer on mount.
    const requestedId = React.useMemo(() => {
        const v = searchParams.get("id");
        if (!v) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }, [searchParams]);
    React.useEffect(() => {
        if (requestedId == null) return;
        if (selectedLead?.id === requestedId) return;
        getLead(requestedId).then(setSelectedLead).catch(() => {
            // unknown id — silently drop the query param
            setSearchParams((p) => { p.delete("id"); return p; });
        });
    }, [requestedId, selectedLead, setSearchParams]);

    const domainsQuery = useQuery({ queryKey: ["domains"], queryFn: () => listDomains() });
    const fieldsQuery = useQuery({ queryKey: ["leads", "fields"], queryFn: fieldsUnion });
    const campaignsQuery = useQuery({
        queryKey: ["campaigns", "leads-filter"],
        queryFn: () => listCampaignsPaged({ size: 200, sort: "name,asc" }),
    });
    const visibleCampaigns = React.useMemo(() => {
        const all = campaignsQuery.data?.content ?? [];
        if (domainId === "all") return all;
        return all.filter((c) => c.domainId === domainId);
    }, [campaignsQuery.data, domainId]);

    React.useEffect(() => {
        if (campaignId === "all") return;
        if (visibleCampaigns.some((c) => c.id === campaignId)) return;
        if (campaignsQuery.data == null) return;
        setCampaignId("all");
    }, [visibleCampaigns, campaignId, campaignsQuery.data]);

    const filters: LeadFilters = {
        page,
        size: PAGE_SIZE,
        domainId: domainId === "all" ? undefined : domainId,
        campaignId: campaignId === "all" ? undefined : campaignId,
        emailContains: emailDebounced || undefined,
        phoneContains: phoneDebounced || undefined,
        advertiserId: ownerFilterParam(advertiserId),
        affiliateId: ownerFilterParam(affiliateId),
    };

    const anyFilterActive =
        domainId !== "all" ||
        campaignId !== "all" ||
        !!emailContains ||
        !!phoneContains ||
        !!primarySource ||
        advertiserId !== undefined ||
        affiliateId !== undefined;

    const resetFilters = (): void => {
        setDomainId("all");
        setCampaignId("all");
        setEmailContains("");
        setPhoneContains("");
        setEmailDebounced("");
        setPhoneDebounced("");
        setPrimarySource(null);
        setAdvertiserId(undefined);
        setAffiliateId(undefined);
    };

    const leadsQuery = useQuery({
        queryKey: ["leads", filters],
        queryFn: () => searchLeads(filters),
        refetchOnMount: "always",
        staleTime: 0,
    });

    React.useEffect(() => {
        setPage(0);
    }, [domainId, campaignId, emailDebounced, phoneDebounced, primarySource, advertiserId, affiliateId]);

    // Client-side primarySource refine on top of the server-filtered page —
    // lets the user narrow without re-fetching. Pagination remains server-side
    // for everything else.
    const filteredRows = React.useMemo(() => {
        const rows = leadsQuery.data?.content ?? [];
        if (!primarySource) return rows;
        return rows.filter((l) => l.primarySource === primarySource);
    }, [leadsQuery.data, primarySource]);

    // ---- Delete / bulk delete (test & spam cleanup) ----------------------
    const deleteMut = useMutation({
        mutationFn: (id: number) => deleteLead(id),
        onSuccess: (_data, id) => {
            toast.success("Lead deleted");
            setSelected((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            // Close the drawer if it was showing this lead, and strip the
            // ?id= deep-link so a reload doesn't chase a deleted row.
            setSelectedLead((cur) => (cur?.id === id ? null : cur));
            if (searchParams.get("id") === String(id)) {
                setSearchParams((p) => { p.delete("id"); return p; }, { replace: true });
            }
            queryClient.invalidateQueries({ queryKey: ["leads"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed")),
    });

    const bulkDeleteMut = useMutation({
        mutationFn: (ids: number[]) => bulkDeleteLeads(ids),
        onSuccess: ({ deleted }) => {
            toast.success(`${deleted} deleted`);
            setSelected(new Set());
            queryClient.invalidateQueries({ queryKey: ["leads"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Bulk delete failed")),
    });

    const confirmDeleteLead = (lead: LeadDto): void => {
        confirm.show({
            title: `Delete lead #${lead.id}?`,
            description:
                "This can't be undone. Leads with billing history are kept — the delete is refused if this lead has commission accruals.",
            destructive: true,
            confirmLabel: "Delete",
            onConfirm: async () => { await deleteMut.mutateAsync(lead.id); },
        });
    };

    const confirmBulkDelete = (): void => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        confirm.show({
            title: `Delete ${ids.length} selected lead${ids.length === 1 ? "" : "s"}?`,
            description:
                "For cleaning out test/spam submissions — this can't be undone. Leads with billing history are kept: the delete is refused if any selected lead has commission accruals.",
            destructive: true,
            confirmLabel: `Delete ${ids.length}`,
            onConfirm: async () => { await bulkDeleteMut.mutateAsync(ids); },
        });
    };

    const pageAllSelected =
        filteredRows.length > 0 && filteredRows.every((l) => selected.has(l.id));
    const togglePageAll = (): void => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (pageAllSelected) for (const l of filteredRows) next.delete(l.id);
            else for (const l of filteredRows) next.add(l.id);
            return next;
        });
    };
    const toggleSelected = (id: number): void => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <>
            <PageHeader
                title="Leads"
                description="Submissions captured from your landing pages."
                actions={
                    <>
                        {selected.size > 0 && (
                            <Button
                                variant="destructive"
                                data-testid="leads-bulk-delete-button"
                                onClick={confirmBulkDelete}
                            >
                                <Trash2 className="h-4 w-4" />
                                Delete selected ({selected.size})
                            </Button>
                        )}
                        <Button
                            data-testid="export-csv-button"
                            onClick={() => {
                                const count = leadsQuery.data?.totalElements ?? 0;
                                confirm.show({
                                    title: `Export ${count.toLocaleString()} lead${count === 1 ? "" : "s"} to CSV?`,
                                    description: anyFilterActive
                                        ? "Exports the current filter scope."
                                        : "Exports EVERY lead on the platform. Apply a filter first if you want a smaller file.",
                                    confirmLabel: "Download",
                                    onConfirm: async () => {
                                        // Trigger download via a synthetic link click; the api/leads.ts
                                        // helper builds the right query string.
                                        const a = document.createElement("a");
                                        a.href = exportCsvUrl(filters);
                                        a.download = "";
                                        a.click();
                                    },
                                });
                            }}
                        >
                            <Download className="h-4 w-4" />
                            Export CSV
                        </Button>
                    </>
                }
            />
            <div className="p-6 space-y-4">
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Search className="h-4 w-4" /> Filters
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                            <div className="space-y-1.5">
                                <Label>Domain</Label>
                                <Select
                                    value={domainId === "all" ? "all" : domainId.toString()}
                                    onValueChange={(v) => setDomainId(v === "all" ? "all" : Number(v))}
                                >
                                    <SelectTrigger data-testid="lead-domain-filter">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All domains</SelectItem>
                                        {domainsQuery.data?.map((d) => (
                                            <SelectItem key={d.id} value={d.id.toString()}>
                                                {d.hostname}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Campaign</Label>
                                <Select
                                    value={campaignId === "all" ? "all" : campaignId.toString()}
                                    onValueChange={(v) => setCampaignId(v === "all" ? "all" : Number(v))}
                                >
                                    <SelectTrigger data-testid="lead-campaign-filter">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All campaigns</SelectItem>
                                        {visibleCampaigns.map((c) => (
                                            <SelectItem key={c.id} value={c.id.toString()}>
                                                {c.name}
                                                {domainId === "all" ? ` · ${c.domainHostname}` : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Email contains</Label>
                                <Input
                                    data-testid="lead-email-filter"
                                    value={emailContains}
                                    onChange={(e) => setEmailContains(e.target.value)}
                                    placeholder="e.g. gmail"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Phone contains</Label>
                                <Input
                                    data-testid="lead-phone-filter"
                                    value={phoneContains}
                                    onChange={(e) => setPhoneContains(e.target.value)}
                                    placeholder="e.g. +1415"
                                />
                            </div>
                            {showAdvertiserColumn && (
                                <div className="space-y-1.5">
                                    <Label>Advertiser</Label>
                                    <AdvertiserSelect
                                        value={advertiserId}
                                        onChange={setAdvertiserId}
                                        data-testid="leads-advertiser-filter"
                                    />
                                </div>
                            )}
                            {showAffiliateColumn && (
                                <div className="space-y-1.5">
                                    <Label>Affiliate</Label>
                                    <AffiliateSelect
                                        value={affiliateId}
                                        onChange={setAffiliateId}
                                        data-testid="leads-affiliate-filter"
                                    />
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <Label>Available fields</Label>
                                <div className="flex flex-wrap gap-1 pt-1">
                                    {fieldsQuery.data && fieldsQuery.data.length > 0 ? (
                                        fieldsQuery.data.map((f) => (
                                            <span key={f} className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                                                {f}
                                            </span>
                                        ))
                                    ) : (
                                        <p className="text-xs text-muted-foreground">None yet</p>
                                    )}
                                </div>
                            </div>
                            {primarySource && (
                                <div className="space-y-1.5">
                                    <Label>Source</Label>
                                    <button
                                        type="button"
                                        onClick={() => setPrimarySource(null)}
                                        className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary hover:bg-primary/20"
                                    >
                                        {primarySource} ✕
                                    </button>
                                    <p className="text-[10px] text-muted-foreground">Note: filtered client-side on this page only.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {leadsQuery.isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size="lg" />
                    </div>
                ) : leadsQuery.data && filteredRows.length > 0 ? (
                    <>
                        <DataTable<LeadDto>
                            dataTestid="leads-table"
                            columns={[
                                {
                                    key: "select",
                                    className: "w-10",
                                    headerClassName: "w-10",
                                    header: (
                                        <input
                                            type="checkbox"
                                            checked={pageAllSelected}
                                            onChange={togglePageAll}
                                            aria-label="Select all leads on this page"
                                            data-testid="leads-select-all"
                                        />
                                    ),
                                    // Rows open the drawer on click — stop the checkbox
                                    // click from bubbling into onRowClick.
                                    cell: (l) => (
                                        <input
                                            type="checkbox"
                                            checked={selected.has(l.id)}
                                            onChange={() => toggleSelected(l.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={`Select lead ${l.id}`}
                                            data-testid={`lead-select-${l.id}`}
                                        />
                                    ),
                                },
                                {
                                    key: "email",
                                    header: "Email",
                                    cell: (l) => <span className="font-medium">{String(l.fields.email ?? "—")}</span>,
                                },
                                {
                                    key: "phone",
                                    header: "Phone",
                                    cell: (l) => <span>{String(l.fields.phone ?? "—")}</span>,
                                },
                                {
                                    key: "domain",
                                    header: "Domain",
                                    // domainHostname comes from the server-enriched DTO (no client-side join).
                                    cell: (l) => (
                                        <span className="text-muted-foreground">
                                            {l.domainHostname ?? `#${l.domainId}`}
                                        </span>
                                    ),
                                },
                                {
                                    key: "campaign",
                                    header: "Campaign",
                                    cell: (l) => l.campaignName ? (
                                        <span className="text-muted-foreground">{l.campaignName}</span>
                                    ) : <span className="text-muted-foreground">—</span>,
                                },
                                ...(showAdvertiserColumn ? [{
                                    key: "advertiser",
                                    header: "Advertiser",
                                    cell: (l: LeadDto) => (
                                        <AdvertiserCell advertiserId={l.advertiserId} lookup={advLookup.byId} />
                                    ),
                                }] : []),
                                ...(showAffiliateColumn ? [{
                                    key: "affiliate",
                                    header: "Affiliate",
                                    cell: (l: LeadDto) => (
                                        <AffiliateCell affiliateId={l.affiliateId} lookup={affLookup.byId} />
                                    ),
                                }] : []),
                                {
                                    key: "source",
                                    header: "Source",
                                    cell: (l) =>
                                        l.primarySource ? (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setPrimarySource(l.primarySource); }}
                                                title="Filter by this source on the current page"
                                                data-testid="lead-row-source"
                                            >
                                                <Badge variant="muted">{l.primarySource}</Badge>
                                            </button>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        ),
                                },
                                {
                                    key: "country",
                                    header: "Country",
                                    cell: (l) => (
                                        <span className="text-muted-foreground">{l.country ?? "—"}</span>
                                    ),
                                },
                                {
                                    key: "preview",
                                    header: "",
                                    cell: (l) =>
                                        l.isPreview ? <Badge variant="warning">preview</Badge> : null,
                                },
                                {
                                    key: "created",
                                    header: "Received",
                                    cell: (l) => (
                                        <span className="text-muted-foreground">
                                            {formatRelativeTime(l.createdAt)}
                                        </span>
                                    ),
                                },
                                {
                                    key: "actions",
                                    header: "",
                                    className: "text-right",
                                    cell: (l) => (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                confirmDeleteLead(l);
                                            }}
                                            title="Delete lead (kept if it has billing history)"
                                            data-testid={`lead-delete-${l.id}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    ),
                                },
                            ]}
                            rows={filteredRows}
                            rowKey={(l) => l.id}
                            onRowClick={(l) => setSelectedLead(l)}
                        />
                        <Pagination
                            page={leadsQuery.data}
                            pageSize={PAGE_SIZE}
                            currentPage={page}
                            onPage={setPage}
                        />
                    </>
                ) : anyFilterActive ? (
                    <EmptyState
                        icon={<Inbox className="h-8 w-8" />}
                        title="No leads match these filters"
                        description="Try clearing one or more filters."
                        action={<Button variant="outline" onClick={resetFilters}>Reset filters</Button>}
                    />
                ) : (
                    <EmptyState
                        icon={<Inbox className="h-8 w-8" />}
                        title="No leads yet"
                        description="Once a visitor submits a form on one of your landing pages, it'll show up here."
                    />
                )}

                <SideDrawer
                    open={selectedLead != null}
                    onClose={() => {
                        setSelectedLead(null);
                        // Strip ?id= so reload doesn't re-open the drawer.
                        if (searchParams.has("id")) {
                            setSearchParams((p) => { p.delete("id"); return p; }, { replace: true });
                        }
                    }}
                    dataTestid="lead-detail-drawer"
                    title={selectedLead ? `Lead #${selectedLead.id}` : ""}
                    subtitle={selectedLead ? formatDate(selectedLead.createdAt) : ""}
                >
                    {selectedLead && (
                        <LeadDetailBody
                            lead={selectedLead}
                            onDelete={() => confirmDeleteLead(selectedLead)}
                        />
                    )}
                </SideDrawer>
            </div>
            {confirm.dialog}
        </>
    );
}

function LeadDetailBody({ lead, onDelete }: { lead: LeadDto; onDelete: () => void }): React.ReactNode {
    const [ftdOpen, setFtdOpen] = React.useState(false);
    const trailQuery = useQuery({
        queryKey: ["lead", lead.id, "visits"],
        queryFn: () => visitTrailForLead(lead.id),
        enabled: lead.visitorId != null,
    });
    const trail = trailQuery.data ?? [];

    return (
        <>
            {/* Related: domain / page / campaign — the operator usually wants to
                jump to one of these next. Shown above the field dump because
                attribution is the single most important question per-lead. */}
            <DrawerSection title="Related">
                <div className="flex flex-wrap gap-2 text-xs">
                    {lead.domainHostname && (
                        <Link to={`/domains?status=ASSIGNED`} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 hover:bg-accent">
                            <ExternalLink className="h-3 w-3" />
                            {lead.domainHostname}
                        </Link>
                    )}
                    {lead.pageId && (
                        <Link to={`/pages/${lead.pageId}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 hover:bg-accent">
                            <ExternalLink className="h-3 w-3" />
                            {lead.pageName ?? `Page #${lead.pageId}`}
                            {lead.pageVersionNum != null && <span className="text-muted-foreground">v{lead.pageVersionNum}</span>}
                        </Link>
                    )}
                    {lead.campaignId && (
                        <Link to={`/campaigns/${lead.campaignId}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 hover:bg-accent">
                            <ExternalLink className="h-3 w-3" />
                            {lead.campaignName ?? `Campaign #${lead.campaignId}`}
                        </Link>
                    )}
                </div>
                {/* Compact commercial action: log an advertiser-reported deposit for this lead. */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setFtdOpen(true)}
                        data-testid="lead-record-ftd-button"
                    >
                        <CircleDollarSign className="h-3.5 w-3.5" /> Record FTD
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        onClick={onDelete}
                        title="Delete this lead (kept if it has billing history)"
                        data-testid="lead-drawer-delete-button"
                    >
                        <Trash2 className="h-3.5 w-3.5" /> Delete lead
                    </Button>
                </div>
            </DrawerSection>

            <DrawerSection title="Fields">
                {Object.entries(lead.fields).map(([k, v]) => (
                    <DrawerRow key={k} label={k} value={String(v)} />
                ))}
            </DrawerSection>

            {lead.visitorId && (
                <DrawerSection
                    title={trail.length > 0 ? `Visit trail · ${trail.length}` : "Visit trail"}
                >
                    {trailQuery.isLoading ? (
                        <p className="text-xs text-muted-foreground">loading…</p>
                    ) : trail.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            No prior visits captured for this visitor (visitor id may be set on
                            this lead but no /v1/visit was logged first).
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {trail.map((v, i) => {
                                const isFirst = i === 0;
                                return (
                                    <div
                                        key={v.id}
                                        className={`rounded-md border p-3 text-xs ${
                                            isFirst ? "border-primary/40 bg-primary/5" : ""
                                        }`}
                                    >
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                {isFirst && (
                                                    <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary-foreground">
                                                        first touch
                                                    </span>
                                                )}
                                                <span className="font-medium">Visit #{v.id}</span>
                                                {v.primarySource && (
                                                    <Badge variant="muted" className="text-[10px]">
                                                        {v.primarySource}
                                                    </Badge>
                                                )}
                                                {v.country && (
                                                    <span className="text-muted-foreground">
                                                        · {v.country}
                                                    </span>
                                                )}
                                            </div>
                                            <span
                                                className="text-muted-foreground"
                                                title={formatDate(v.createdAt)}
                                            >
                                                {formatRelativeTime(v.createdAt)}
                                            </span>
                                        </div>
                                        {v.clickIds && Object.keys(v.clickIds).length > 0 && (
                                            <div className="mt-1.5 font-mono text-[10px] text-muted-foreground">
                                                {Object.entries(v.clickIds)
                                                    .map(([k, val]) => `${k}=${val}`)
                                                    .join(" · ")}
                                            </div>
                                        )}
                                        {v.landingUrl && (
                                            <div className="mt-1 truncate font-mono text-[10px] text-muted-foreground">
                                                {v.landingUrl}
                                            </div>
                                        )}
                                        {v.referrer && (
                                            <div className="mt-0.5 text-[10px] text-muted-foreground">
                                                ← {v.referrer}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </DrawerSection>
            )}

            <CapturedData record={lead} />

            <RecordFtdDialog leadId={lead.id} open={ftdOpen} onOpenChange={setFtdOpen} />
        </>
    );
}
